import { Field, FieldProps } from 'formik';
import { FormHelperText, Grid } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolSelect, PoolTextField, PoolTypography, SelectOption } from '@synergy/components';
import { fields } from '@synergy/utils';

import { ProductDetailOverviewFormValues } from '../models/product-detail-overview-form-values';
import { ProductDetailOverviewSectionPackingUnits } from './product-detail-overview-section-packing-units';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

export const ProductDetailOverviewSectionOrder = () => {
    const { allUnits: units, requesting: unitsRequesting } = useAppSelector(state => state.units);
    const currentLocale = useAppSelector(state => state.currentLocale);

    const fieldNames = fields<ProductDetailOverviewFormValues>();

    const selectableUnits = units.map<SelectOption>(unit => {
        return {
            value: unit.id,
            displayValue: unit.translations[currentLocale],
        };
    });

    return (
        <>
            <PoolTypography styleType="sectionTitle" id="app.product.detail.orderDetails" />
            <Grid container={true} spacing={2}>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.orderUnitId}>
                        {({ form, field, meta }: FieldProps<number>) => (
                            <>
                                <PoolSelect
                                    name={fieldNames.orderUnitId}
                                    fullWidth={true}
                                    label={<FormattedMessage id="app.label.orderUnit" />}
                                    options={selectableUnits}
                                    onValueChange={value => form.setFieldValue(field.name, value)}
                                    error={meta.touched && !!meta.error}
                                    required={true}
                                    value={field.value}
                                    noSorting={true}
                                    disabled={unitsRequesting}
                                />
                                {meta.error && (
                                    <FormHelperText error={true}>
                                        <FormattedMessage id={meta.error} />
                                    </FormHelperText>
                                )}
                            </>
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.contentUnitId}>
                        {({ form, field, meta }: FieldProps<number>) => (
                            <>
                                <PoolSelect
                                    name={fieldNames.contentUnitId}
                                    fullWidth={true}
                                    label={<FormattedMessage id="app.label.contentUnit" />}
                                    options={selectableUnits}
                                    onValueChange={value => form.setFieldValue(field.name, value)}
                                    error={meta.touched && !!meta.error}
                                    value={field.value}
                                    noSorting={true}
                                    disabled={unitsRequesting}
                                />
                                {meta.error && (
                                    <FormHelperText error={true}>
                                        <FormattedMessage id={meta.error} />
                                    </FormHelperText>
                                )}
                            </>
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.contentQuantity}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.label.contentQuantity" />}
                                error={!!meta.error}
                                helperText={meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                                isNumeric={true}
                                noNegativeNumbers={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={3}>
                    <Field name={fieldNames.minimumOrderQuantity}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.label.minimumOrderQuantity" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                                isNumeric={true}
                                noNegativeNumbers={true}
                                required={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={3}>
                    <Field name={fieldNames.maximumOrderQuantity}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.label.maximumOrderQuantity" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                                isNumeric={true}
                                noNegativeNumbers={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={3}>
                    <Field name={fieldNames.recommendedOrderQuantity}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.label.recommendedOrderQuantity" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                                isNumeric={true}
                                noNegativeNumbers={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={3}>
                    <Field name={fieldNames.quantityInterval}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.label.quantityInterval" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                                isNumeric={true}
                                noNegativeNumbers={true}
                                required={true}
                            />
                        )}
                    </Field>
                </Grid>

                <Grid item={true} xs={12}>
                    <ProductDetailOverviewSectionPackingUnits />
                </Grid>
            </Grid>
        </>
    );
};
